import { EdrSensorModule } from './uiSettings/edrSensor/shared-dtos-edr-sensor-common';
import { PatchManagementModule } from './uiSettings/patchManagement/shared-dtos-patch-management-common';
import { IntegrityMonitorModule } from './uiSettings/integrityMonitor/shared-dtos-integrity-monitor-common';
import { EncryptionModule } from './uiSettings/encryption/shared-dtos-encryption-common';
import { StorageProtectionModule } from './uiSettings/storageProtection/shared-dtos-storage-protection-common';
import {
  IndicatorsOfRiskModule,
  PhasrModule,
} from './uiSettings/indicatorsOfRisk/shared-dtos-indicators-of-risk-common';
import { DeviceControlModule } from './uiSettings/deviceControl/shared-dtos-device-control-common';
import { RelayModule } from './uiSettings/relay/shared-dtos-relay-common';
import { LiveSearchModule } from './uiSettings/liveSearch/shared-dtos-live-search-common';
import {
  GhostrModule,
  IcapModule,
  NetworkSandboxingModule,
  SandboxManagerModule,
} from './uiSettings/sandbox/shared-dtos-sandbox-common';
import { ApplicationControlModule } from './uiSettings/applicationControl/shared-dtos-application-control-common';
import { FirewallModule } from './uiSettings/firewall/shared-dtos-firewall-common';
import { ContentControlModule } from './uiSettings/contentControl/shared-dtos-content-control-common';
import { GeneralModule } from './uiSettings/general/shared-dtos-general-common';
import { ExchangeProtectionModule } from './uiSettings/exchange/shared-dtos-exchange-protection-common';
import { BlocklistRulesModule } from "./uiSettings/blocklistRules/shared-dtos-blocklist-rules-common";
import { AntimalwareModule } from './uiSettings/antimalware/shared-dtos-antimalware-common';
import { NsxModule } from './uiSettings/nsx/shared-dtos-nsx-common';

export interface UiSettings extends Object {
  [ModuleId.GENERAL]: GeneralModule;
  [ModuleId.ANTIMALWARE]: AntimalwareModule;
  [ModuleId.PATCH_MANAGEMENT]: PatchManagementModule;
  [ModuleId.INTEGRITY_MONITOR]: IntegrityMonitorModule;
  [ModuleId.EDR_SENSOR]: EdrSensorModule;
  [ModuleId.ENCRYPTION]?: EncryptionModule;
  [ModuleId.STORAGE_PROTECTION]: StorageProtectionModule;
  [ModuleId.INDICATORS_OF_RISK]?: IndicatorsOfRiskModule;
  [ModuleId.PHASR]?: PhasrModule;
  [ModuleId.DEVICE_CONTROL]: DeviceControlModule;
  [ModuleId.RELAY]: RelayModule;
  [ModuleId.LIVE_SEARCH]?: LiveSearchModule;
  networkSandboxing?: NetworkSandboxingModule; // is in the sandbox module
  [SectionId.NETWORK_SENSOR]?: GhostrModule; // is in the sandbox module
  [SectionId.ICAP_SENSOR]?: IcapModule; // is in the sandbox module
  [SectionId.SANDBOX_MANAGER]?: SandboxManagerModule; // is in the sandbox module
  [ModuleId.APPLICATION_CONTROL]?: ApplicationControlModule;
  [ModuleId.FIREWALL]: FirewallModule;
  [ModuleId.CONTENT_CONTROL]: ContentControlModule;
  [ModuleId.EXCHANGE_PROTECTION]?: ExchangeProtectionModule;
  [ModuleId.BLOCKLIST_RULES]?: BlocklistRulesModule;
  [ModuleId.NSX]?: NsxModule;
}

export interface Inheritance {
  policyName: string;
  policyId: string;
  sectionId: SectionId | null;
  moduleId: ModuleId;
}

export interface InheritancePolicyData {
  _id: string;
  name: string;
  inheritance: Inheritance[];
}

export interface Inheritor {
  id: string;
  name: string;
}

export interface Policy {
  _id: string;
  name: string;
  uiSettings: UiSettings;
  readOnly: boolean;
  ownerFullName: string;
  ownerId: string;
  ownerUserName: string;
  created: string;
  lastModified: string;
  company: {
    id: string;
    name: string;
  } | null;
  clonedAfter?: string;
  inheritance: Inheritance[];
  inheritors: Inheritor[];
  default?: boolean;
  service: number;
  policyVersion?: number;
  myDefaultPolicy?: boolean;
  noOwner?: boolean;
}

export enum NsxErrorType {
  NSX_VCENTER_FAILED_TO_CONNECT = 1,
  NSX_VCENTER_UNRECOGNIZED_CERTIFICATE = 2,
}

export interface PolicyCounters {
  targets?: number;
  active?: number;
  applied?: number;
  pending?: number;

  // nsx
  allowDelete?: boolean;
  error?: NsxErrorType;
  nsxHost?: string;
  vCenterName?: string;
  externallyApplied?: number;
}

export interface TreePath {
  moduleId: ModuleId;
  sectionId: SectionId;
}

// see: inheritableEndpointModules from ExtJs
export enum ModuleId {
  POLICY_SETTINGS = 'policy',
  GENERAL = 'general',
  ANTIMALWARE = 'antimalware',
  PATCH_MANAGEMENT = 'patchManagement',
  INTEGRITY_MONITOR = 'integrityMonitor',
  EDR_SENSOR = 'edrSensor',
  LIVE_SEARCH = 'liveSearch',
  ENCRYPTION = 'encryption',
  STORAGE_PROTECTION = 'storageProtection',
  INDICATORS_OF_RISK = 'indicatorsOfRisk',
  DEVICE_CONTROL = 'deviceControl',
  RELAY = 'relay',
  SANDBOX = 'sandboxAnalyzer',
  APPLICATION_CONTROL = 'applicationControl',
  NSX = 'nsx',
  FIREWALL = 'firewall',
  CONTENT_CONTROL = 'contentControl',
  PHASR = 'phasr',
  EXCHANGE_PROTECTION = 'exchange',
  BLOCKLIST_RULES = 'blocklistRules',
}

// see: inheritableEndpointSections from ExtJs
export enum SectionId {
  SECURITY_TELEMETRY = 'securityTelemetry',
  ANTIMALWARE_ANTI_TAMPERING = 'antiTampering',
  ANTIMALWARE_ON_ACCESS = 'onAccess',
  ANTIMALWARE_DYNAMIC_THREAT_DEFENSE = 'dynamicThreatDefense',
  ANTIMALWARE_SECURITY_SERVERS = 'securityServers',
  ANTIMALWARE_ANTI_EXPLOIT = 'antiExploit',
  ANTIMALWARE_ON_EXECUTE = 'onExecute',
  ANTIMALWARE_ON_DEMAND = 'onDemand',
  GENERAL_DISPLAY = 'display',
  INTEGRITY_MONITOR_REAL_TIME = 'realTime',
  GENERAL = 'general',
  STORAGE_PROTECTION_ICAP = 'icap',
  COMMUNICATION = 'communication',
  UPDATE = 'update',
  ENDPOINT_SENSOR = 'localSubmission',
  NETWORK_SENSOR = 'ghostr',
  ICAP_SENSOR = 'icapSensor',
  SANDBOX_MANAGER = 'sandboxManager',
  SETTINGS = 'settings',
  FIREWALL_NETWORK = 'network',
  FIREWALL_ADVANCED = 'advanced',
  CONTENT_CONTROL_TRAFFIC = 'traffic',
  CONTENT_CONTROL_DATA_PROTECTION = 'dataProtection',
  CONTENT_CONTROL_NETWORK_MONITOR = 'networkMonitor',
  CONTENT_CONTROL_WEB = 'web',
  NONE = '', // use it for modules without sections
  RULES = 'rules',
  EXCLUSIONS = 'exclusions',
  EXCHANGE_ANTIMALWARE = 'antimalware',
  EXCHANGE_CONTENT_CONTROL = 'contentControl',
  EXCHANGE_ANTISPAM = 'antispam',
  DETAILS = 'details',
  GENERAL_NOTIFICATIONS = 'notifications',
  GENERAL_INHERITANCE = 'inheritance',
  ALL = 'all',
}
